import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ComparisonMode = ({ onClose }) => {
  const [selectedBacktests, setSelectedBacktests] = useState(['bt1', 'bt2']);
  const [comparisonMetric, setComparisonMetric] = useState('equity');

  const availableBacktests = [
    { value: 'bt1', label: 'Golden Cross MA - BTC/USDT (Current)' },
    { value: 'bt2', label: 'RSI Oversold - BTC/USDT (Dec 10)' },
    { value: 'bt3', label: 'Bollinger Bands - ETH/USDT (Dec 8)' },
    { value: 'bt4', label: 'MACD Divergence - BTC/USDT (Dec 5)' },
    { value: 'bt5', label: 'Grid Trading - BNB/USDT (Dec 3)' }
  ];

  const metricOptions = [
    { value: 'equity', label: 'Equity Curve' },
    { value: 'returns', label: 'Monthly Returns' },
    { value: 'drawdown', label: 'Drawdown' },
    { value: 'trades', label: 'Trade Count' }
  ];

  const comparisonData = [
    { date: '2024-01-01', bt1: 10000, bt2: 10000, bt3: 10000 },
    { date: '2024-02-01', bt1: 10850, bt2: 10420, bt3: 10650 },
    { date: '2024-03-01', bt1: 11200, bt2: 10890, bt3: 10980 },
    { date: '2024-04-01', bt1: 10950, bt2: 11250, bt3: 10750 },
    { date: '2024-05-01', bt1: 12100, bt2: 11680, bt3: 11890 },
    { date: '2024-06-01', bt1: 11800, bt2: 12100, bt3: 11650 },
    { date: '2024-07-01', bt1: 13200, bt2: 12450, bt3: 12800 },
    { date: '2024-08-01', bt1: 12900, bt2: 12890, bt3: 12550 },
    { date: '2024-09-01', bt1: 14500, bt2: 13650, bt3: 14100 },
    { date: '2024-10-01', bt1: 15200, bt2: 14200, bt3: 14850 },
    { date: '2024-11-01', bt1: 16800, bt2: 15100, bt3: 16200 },
    { date: '2024-12-01', bt1: 18450, bt2: 16800, bt3: 17650 }
  ];

  const performanceComparison = [
    {
      strategy: 'Golden Cross MA',
      totalReturn: 84.5,
      sharpeRatio: 2.34,
      maxDrawdown: -4.1,
      winRate: 68.2,
      totalTrades: 247,
      profitFactor: 2.15,
      color: '#FFD700'
    },
    {
      strategy: 'RSI Oversold',
      totalReturn: 68.0,
      sharpeRatio: 1.89,
      maxDrawdown: -6.2,
      winRate: 72.5,
      totalTrades: 189,
      profitFactor: 1.87,
      color: '#00FF88'
    },
    {
      strategy: 'Bollinger Bands',
      totalReturn: 76.5,
      sharpeRatio: 2.12,
      maxDrawdown: -5.1,
      winRate: 65.8,
      totalTrades: 203,
      profitFactor: 2.03,
      color: '#FFA500'
    }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value?.toFixed(2)}%`;
  };

  const getStrategyColor = (index) => {
    const colors = ['#FFD700', '#00FF88', '#FFA500', '#FF4444', '#8B5CF6'];
    return colors?.[index % colors?.length];
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-secondary">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="GitCompare" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Strategy Comparison</h2>
              <p className="text-sm text-muted-foreground">Compare multiple backtest results side by side</p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Configuration */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Select
              label="Select Strategies to Compare"
              options={availableBacktests}
              value={selectedBacktests}
              onChange={setSelectedBacktests}
              multiple
              searchable
              description="Choose up to 5 strategies for comparison"
            />
            
            <Select
              label="Comparison Metric"
              options={metricOptions}
              value={comparisonMetric}
              onChange={setComparisonMetric}
              description="Select the primary metric to visualize"
            />
          </div>

          {/* Performance Metrics Comparison */}
          <div className="bg-muted/30 border border-border-secondary rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-foreground mb-6">Performance Metrics Comparison</h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-secondary">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Strategy</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Total Return</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Sharpe Ratio</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Max Drawdown</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Win Rate</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Total Trades</th>
                    <th className="text-right p-4 text-sm font-medium text-muted-foreground">Profit Factor</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceComparison?.map((strategy, index) => (
                    <tr key={index} className="border-b border-border-secondary hover:bg-muted/20 trading-transition">
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: strategy?.color }} />
                          <span className="font-medium text-foreground">{strategy?.strategy}</span>
                        </div>
                      </td>
                      <td className="text-right p-4">
                        <span className={`font-medium ${strategy?.totalReturn > 0 ? 'text-success' : 'text-error'}`}>
                          {formatPercentage(strategy?.totalReturn)}
                        </span>
                      </td>
                      <td className="text-right p-4 text-foreground font-medium">{strategy?.sharpeRatio}</td>
                      <td className="text-right p-4 text-error font-medium">{formatPercentage(strategy?.maxDrawdown)}</td>
                      <td className="text-right p-4 text-foreground font-medium">{strategy?.winRate}%</td>
                      <td className="text-right p-4 text-foreground font-medium">{strategy?.totalTrades}</td>
                      <td className="text-right p-4 text-foreground font-medium">{strategy?.profitFactor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comparison Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Equity Curve Comparison */}
            <div className="bg-muted/30 border border-border-secondary rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Equity Curve Comparison</h3>
                <div className="flex items-center space-x-4">
                  {performanceComparison?.slice(0, 3)?.map((strategy, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: strategy?.color }} />
                      <span className="text-xs text-muted-foreground">{strategy?.strategy}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#B0B0B0"
                      fontSize={12}
                      tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short' })}
                    />
                    <YAxis 
                      stroke="#B0B0B0"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}K`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1A1A1A',
                        border: '1px solid rgba(255,215,0,0.2)',
                        borderRadius: '8px',
                        color: '#FFFFFF'
                      }}
                      formatter={(value, name) => [formatCurrency(value), name]}
                      labelFormatter={(value) => new Date(value)?.toLocaleDateString()}
                    />
                    <Line
                      type="monotone"
                      dataKey="bt1"
                      stroke="#FFD700"
                      strokeWidth={2}
                      dot={false}
                      name="Golden Cross MA"
                    />
                    <Line
                      type="monotone"
                      dataKey="bt2"
                      stroke="#00FF88"
                      strokeWidth={2}
                      dot={false}
                      name="RSI Oversold"
                    />
                    <Line
                      type="monotone"
                      dataKey="bt3"
                      stroke="#FFA500"
                      strokeWidth={2}
                      dot={false}
                      name="Bollinger Bands"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Returns Comparison */}
            <div className="bg-muted/30 border border-border-secondary rounded-lg p-6">
              <h3 className="font-semibold text-foreground mb-4">Total Returns Comparison</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performanceComparison} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis type="number" stroke="#B0B0B0" fontSize={12} tickFormatter={(value) => `${value}%`} />
                    <YAxis type="category" dataKey="strategy" stroke="#B0B0B0" fontSize={12} width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1A1A1A',
                        border: '1px solid rgba(255,215,0,0.2)',
                        borderRadius: '8px',
                        color: '#FFFFFF'
                      }}
                      formatter={(value) => [`${value}%`, 'Total Return']}
                    />
                    <Bar
                      dataKey="totalReturn"
                      fill="#FFD700"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Risk Metrics Comparison */}
          <div className="bg-muted/30 border border-border-secondary rounded-lg p-6 mt-8">
            <h3 className="font-semibold text-foreground mb-6">Risk-Adjusted Performance</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {performanceComparison?.map((strategy, index) => (
                <div key={index} className="bg-background/50 border border-border-secondary rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: strategy?.color }} />
                    <h4 className="font-medium text-foreground">{strategy?.strategy}</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                      <span className="text-sm font-medium text-foreground">{strategy?.sharpeRatio}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Max Drawdown</span>
                      <span className="text-sm font-medium text-error">{formatPercentage(strategy?.maxDrawdown)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Profit Factor</span>
                      <span className="text-sm font-medium text-foreground">{strategy?.profitFactor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Win Rate</span>
                      <span className="text-sm font-medium text-success">{strategy?.winRate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border-secondary">
            <div className="flex items-center space-x-4">
              <Button variant="outline" iconName="Download" iconPosition="left">
                Export Comparison
              </Button>
              <Button variant="outline" iconName="Save" iconPosition="left">
                Save Analysis
              </Button>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button iconName="Rocket" iconPosition="left">
                Deploy Best Strategy
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonMode;